/**
 * useOAuthConfig Hook
 * 
 * Fetches the OAuth configuration to determine which method to use:
 * - Emergent OAuth (default)
 * - Custom Google OAuth
 */
import { useState, useEffect } from 'react';
import api from '../services/api';

export const useOAuthConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/api/admin/system-settings/oauth-config');
        setConfig(response.data);
      } catch (err) {
        console.error('Failed to fetch OAuth config:', err);
        // Default to Emergent OAuth if config fetch fails
        setConfig({
          method: 'emergent',
          auth_url: 'https://auth.emergentagent.com/'
        });
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  /**
   * Get the OAuth redirect URL based on configuration
   * @param {string} redirectAfter - URL to redirect to after OAuth
   * @param {object} signupPricing - Optional pricing data for paid plan signups (custom OAuth only)
   * @returns {string} The OAuth URL to redirect to
   */
  const getOAuthUrl = (redirectAfter, signupPricing = null) => {
    if (!config) {
      // Fallback to Emergent while loading
      return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectAfter)}`;
    }

    if (config.method === 'custom') {
      // Custom Google OAuth - construct the URL
      // State can contain both redirect URL and signup pricing
      let stateData;
      if (signupPricing) {
        stateData = JSON.stringify({
          redirect_after: redirectAfter || '',
          signup_pricing: signupPricing
        });
      } else {
        stateData = redirectAfter || '';
      }
      
      const params = new URLSearchParams({
        client_id: config.client_id,
        redirect_uri: config.redirect_uri,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'offline',
        state: stateData,
        prompt: 'select_account'
      });
      return `${config.auth_url}?${params.toString()}`;
    } else {
      // Emergent OAuth
      return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectAfter)}`;
    }
  };

  /**
   * Check if using custom Google OAuth
   */
  const isCustomOAuth = () => {
    return config?.method === 'custom';
  };

  return {
    config,
    loading,
    error,
    getOAuthUrl,
    isCustomOAuth
  };
};

export default useOAuthConfig;
